import { Button, Collapse } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState, memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { AddSharp as PlusIcon } from '@material-ui/icons';
import ExpandIcon from '@material-ui/icons/ExpandMoreOutlined';
import { Formik } from 'formik';
import { DateTime } from 'luxon';
import { useDialogState, useMenuState } from '~/modules/common/hooks';
import { useHasPermission } from '~/modules/common/permissions';
import { getEffectiveSchedule } from '~/modules/common/dates/schedule';
import useProjectDetails from '~/modules/quickAllocation/components/QuickAllocationUserChart/hooks/useProjectDetails';
import { SortDirection, ResourceAllocationStatus } from '~/types';
import {
  useQuickAllocationContext,
  useResourceAllocationsSummary,
  useFulfilledResourceAllocationHandler,
  useResourceProjectActualSeriesForUser
} from '~/modules/quickAllocation/components/QuickAllocationUserChart/hooks';

import ShowSmartFitDialogOrLoading from '~/modules/resourcing/components/ResourceAssignmentDialog/components/ResourceUsersChartPanel/components/ShowSmartFitDialogOrLoading';
import { buildResourceAllocation } from '~/modules/resourcing/hooks/useCreateResourceAllocationHandler';
import { useGetScheduleDurationByDayForUsers } from '~/modules/resourcing/common/hooks';
import { getAllocationDatesFromProjectDate } from '~/modules/resourcing/common/util/resourceAllocationUtil';
import { useMeContext } from '~/modules/me';
import AllocateResourcePopup from '~/modules/resourcing/components/AllocateResourcePopup/AllocateResourcePopup';
import { useAllocateResourcePopupHandler } from '~/modules/resourcing/components/AllocateResourcePopup/hooks/useAllocateResourcePopupHandler';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import { PERIOD_SCALE_ENUM } from '~/modules/resourcing/common/enums';
import { useGetScheduleRulesForUserAndDateRange } from '~/modules/resourcing/common/hooks/useGetScheduleRulesForUserAndDateRange';
import QuickAllocationProjectGroupHeader from '../QuickAllocationProjectGroupHeader';
import QuickAllocationProjectChartRowsLoading from './QuickAllocationProjectChartRowsLoading';
import QuickAllocationProjectGroupDetailRow from './QuickAllocationProjectGroupDetailRow';
import useStyles, { useSkeletonStyles } from './useStyles';

const QuickAllocationProjectGroupDetail = ({
  handleAllocationPeriodClick,
  holidaySummaryPeriods,
  rangeMaxDate,
  rangeMinDate,
  resourceAvailabilitySummarySeries,
  timeoffSummaryPeriods,
  user
}) => {
  const {
    chartDates,
    chartStartDate,
    dateRange,
    isShowActualEnabled,
    leftComponentWidth,
    projectStatusFilterOption,
    scale,
    setAvailabilityLoading
  } = useQuickAllocationContext();
  const [newDraftResourceAllocation, setNewDraftResourceAllocation] = useState(
    {}
  );
  const { anchorEl, onMenuClose, onMenuClick } = useMenuState();
  const me = useMeContext();

  const [sort, setSort] = useState({
    field: 'projectName',
    direction: SortDirection.Asc
  });

  const onHandleAllocationPeriodClick = useCallback(
    param => {
      handleAllocationPeriodClick({ ...param, allocationSort: sort });
    },
    [handleAllocationPeriodClick, sort]
  );

  const classes = useStyles();
  const skeltonClasses = useSkeletonStyles();

  const { fetchProjectDetails } = useProjectDetails();

  const {
    onCreateResourceAllocation,
    loading: loadingAllocationSubmission
  } = useFulfilledResourceAllocationHandler({
    allocationSort: sort
  });

  const isResourceAssignmentEnabled = useHasPermission({
    actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
  });

  const effectiveSchedule = getEffectiveSchedule(user.roleSchedules || []);
  const primaryRole = (effectiveSchedule.projectRoles || []).find(
    role => role.isPrimary
  );

  const projectRole = useMemo(
    () =>
      primaryRole && primaryRole.projectRole
        ? {
            id: primaryRole.projectRole.uri,
            displayText: primaryRole.projectRole.name
          }
        : null,
    [primaryRole]
  );

  const resourceUser = useMemo(
    () => ({
      id: user.id,
      uri: user.uri,
      displayText: user.displayText
    }),
    [user.id, user.uri, user.displayText]
  );

  const {
    loadingAllocations,
    loadingMore,
    loadMore,
    nextPageCursor,
    allocations
  } = useResourceAllocationsSummary({
    scale,
    sort,
    dateRange,
    userUri: user.uri,
    projectStatusFilterOption
  });

  const {
    isLoading: isActualsLoading,
    resourceUserActualSeriesByUser,
    loadMoreActualRows
  } = useResourceProjectActualSeriesForUser({
    scale,
    sort,
    dateRange,
    userUri: user.uri,
    projectStatusFilterOption,
    isResourceActualModeEnabled: isShowActualEnabled
  });

  const loadMoreAllocationsAndActuals = useCallback(() => {
    if (loadMoreActualRows) loadMoreActualRows();
    if (loadMore) loadMore();
  }, [loadMore, loadMoreActualRows]);

  const {
    scheduleDurations = [],
    loadingRows: scheduleLoading
  } = useGetScheduleDurationByDayForUsers({
    userIds: [user.uri],
    dateRange: {
      startDate: chartDates[0].start,
      endDate: chartDates[chartDates.length - 1].end
    }
  });
  const {
    open: openSmartFitDialogOrLoadingState,
    closeDialog: closeShowSmartFitDialog,
    openDialog: openShowSmartFitDialogOrLoading
  } = useDialogState(false);

  const closeShowSmartFitDialogOrLoading = useCallback(() => {
    closeShowSmartFitDialog();
    setNewDraftResourceAllocation({});
  }, [closeShowSmartFitDialog]);

  const handleOnClose = useCallback(
    (event, inMemoryAllocation) => {
      onMenuClose(event);
      if (inMemoryAllocation) {
        openShowSmartFitDialogOrLoading();
      }
    },
    [onMenuClose, openShowSmartFitDialogOrLoading]
  );

  const createAllocationWithUserAndOverrideRules = useCallback(
    overrideScheduleRules => {
      const allocation = buildResourceAllocation({
        user,
        scheduleRules: overrideScheduleRules,
        projectUri: newDraftResourceAllocation.project.id,
        roleUri: newDraftResourceAllocation.roleUri,
        me,
        allocationStatus: ResourceAllocationStatus.Committed
      });

      closeShowSmartFitDialogOrLoading();

      onCreateResourceAllocation({ allocation });
      setAvailabilityLoading();
    },
    [
      closeShowSmartFitDialogOrLoading,
      me,
      newDraftResourceAllocation,
      onCreateResourceAllocation,
      setAvailabilityLoading,
      user
    ]
  );

  const defaultDates = useMemo(() => {
    const startDate = DateTime.utc();
    const defaultEndDateScale = scale
      ? scale === PERIOD_SCALE_ENUM.DAYS
        ? PERIOD_SCALE_ENUM.WEEKS
        : scale
      : PERIOD_SCALE_ENUM.MONTHS;
    const endDate = startDate
      .plus({ [defaultEndDateScale]: 1 })
      .minus({ days: scale ? 1 : 0 });

    return {
      startDate: dateToMidnightUTCString(startDate),
      endDate: dateToMidnightUTCString(endDate)
    };
  }, [scale]);

  const {
    allocationStartDate,
    allocationEndDate
  } = getAllocationDatesFromProjectDate({
    projectStartDate: newDraftResourceAllocation.project?.startDate,
    projectEndDate: newDraftResourceAllocation.project?.endDate,
    scale
  });

  const {
    scheduledHours: initialScheduledHours,
    scheduleRules
  } = useGetScheduleRulesForUserAndDateRange({
    userUri: user.uri,
    dateRange: {
      startDate: allocationStartDate,
      endDate: allocationEndDate
    }
  });

  const [totalScheduledHours, setTotalScheduledHours] = useState(
    initialScheduledHours
  );
  const {
    initialValues,
    handleAdd,
    validationSchema
  } = useAllocateResourcePopupHandler({
    scale,
    defaultDates,
    setNewDraftResourceAllocation,
    user,
    projectRole,
    totalScheduledHours: initialScheduledHours,
    scheduleRules
  });

  const handleClick = useCallback(
    event => {
      setTotalScheduledHours(initialScheduledHours);
      onMenuClick(event);
    },
    [onMenuClick, initialScheduledHours]
  );

  if (loadingAllocations || scheduleLoading || isActualsLoading)
    return <QuickAllocationProjectChartRowsLoading classes={skeltonClasses} />;

  const resourceAllocationsWithScheduleDurations = allocations.map(alloc => ({
    ...alloc,
    resourceAllocation: {
      ...alloc.resourceAllocation,
      user: {
        ...alloc.resourceAllocation.user,
        scheduleDurationByDay: scheduleDurations[0]?.scheduleDurationByDay || []
      }
    }
  }));

  return (
    <Collapse in timeout="auto" unmountOnExit>
      <div
        data-qe-id={`${user.displayText}_Project`}
        className={classes.rootExpanded}
      >
        <QuickAllocationProjectGroupHeader
          scale={scale}
          chartDates={chartDates}
          leftComponentWidth={leftComponentWidth}
          onSortChange={setSort}
          sort={sort}
          isRoleColumnSortable
        />
        {(resourceAllocationsWithScheduleDurations || []).map(entry => (
          <QuickAllocationProjectGroupDetailRow
            key={entry.resourceAllocation.id}
            rangeMinDate={rangeMinDate}
            rangeMaxDate={rangeMaxDate}
            resourceUser={user}
            resourceAllocation={entry.resourceAllocation}
            resourceAllocatedHoursByDateRange={
              entry.resourceAllocatedHoursByDateRange
            }
            holidaySummaryPeriods={holidaySummaryPeriods}
            timeoffSummaryPeriods={timeoffSummaryPeriods}
            allocationSort={sort}
            resourceAvailabilitySummarySeries={
              resourceAvailabilitySummarySeries
            }
            resourceUserActualSeries={(
              resourceUserActualSeriesByUser || []
            ).find(
              e =>
                e.project.id.includes(entry.resourceAllocation.projectUri) &&
                (e.role?.id.includes(entry.resourceAllocation.role?.id) ||
                  (entry.resourceAllocation.role === null && e.role === null))
            )}
            handleAllocationPeriodClick={onHandleAllocationPeriodClick}
          />
        ))}
        {!allocations.length > 0 && (
          <>
            <span className={classes.noneMessage}>
              <FormattedMessage id="quickAllocation.noProjectAllocated" />
            </span>
          </>
        )}
        {(loadingMore || loadingAllocationSubmission) && (
          <QuickAllocationProjectChartRowsLoading classes={skeltonClasses} />
        )}
        <div className={classes.bottomRow}>
          <div className={classes.addProjectButton}>
            {isResourceAssignmentEnabled && (
              <>
                <Button
                  color="primary"
                  className={classes.showMoreButtonText}
                  onClick={handleClick}
                >
                  <PlusIcon size="small" />
                  <FormattedMessage id="quickAllocation.addToProject" />
                </Button>
                {anchorEl && (
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize={false}
                    onSubmit={handleAdd}
                    validate={validationSchema}
                  >
                    <AllocateResourcePopup
                      anchorEl={anchorEl}
                      onClose={handleOnClose}
                      fetchProjectDetails={fetchProjectDetails}
                      handleAdd={handleAdd}
                      showAddUser={false}
                      showAddProject
                      totalScheduledHours={totalScheduledHours}
                      setTotalScheduledHours={setTotalScheduledHours}
                      scale={scale}
                    />
                  </Formik>
                )}
              </>
            )}
          </div>
          <div className={classes.showMoreButton}>
            {!loadingAllocations && Boolean(nextPageCursor) && (
              <Button
                className={classes.showMoreButtonText}
                color="primary"
                onClick={loadMoreAllocationsAndActuals}
              >
                <FormattedMessage id="quickAllocation.showMore" />
                <ExpandIcon className={classes.icon} role="img" />
              </Button>
            )}
          </div>
        </div>
      </div>
      {openSmartFitDialogOrLoadingState && (
        <ShowSmartFitDialogOrLoading
          scale={scale}
          resourceUser={resourceUser}
          scheduleRules={newDraftResourceAllocation.scheduleRules}
          closeShowSmartFitDialogOrLoading={closeShowSmartFitDialogOrLoading}
          projectDefaultScheduleRule={
            newDraftResourceAllocation.project.defaultScheduleRule
          }
          chartStartDate={chartStartDate}
          createAllocationWithUserAndOverrideRules={
            createAllocationWithUserAndOverrideRules
          }
        />
      )}
    </Collapse>
  );
};

QuickAllocationProjectGroupDetail.propTypes = {
  user: PropTypes.object,
  rangeMinDate: PropTypes.object,
  rangeMaxDate: PropTypes.object,
  timeoffSummaryPeriods: PropTypes.array,
  holidaySummaryPeriods: PropTypes.array,
  resourceAvailabilitySummarySeries: PropTypes.array,
  handleAllocationPeriodClick: PropTypes.func
};

export default memo(QuickAllocationProjectGroupDetail);
